import React, { useEffect } from 'react';
import {
    OpportunityAssistantDrawer,
    FinancingGridCard,
    AddTitleContactsModal,
} from '..';
import BlackKnightLeadModal from '../BlackKnightLeadModal';
import DashboardModal from '../DashboardModal';
import { OpportunityValidationModal } from '../OpportunityValidation/OpportunityValidationModal';
import {
    OBD_PRICING_GRID_MODAL_Z,
    OBD_PRICING_GRID_NESTED_MODAL_Z,
} from '../oneButtonOverlayZ';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';

interface DashboardModalsSectionProps {
    initialDashboardReady: boolean;
    isDashboardOpen: boolean;
    opportunityAssistantOpen: boolean;
    setOpportunityAssistantOpen: (next: boolean) => void;
    pricingGridModalOpen: boolean;
    setPricingGridModalOpen: (next: boolean) => void;
    theme: string;
    pricingFetchTrigger: number | null;
    pricingDesignForTrigger: any;
    priceQuote: any;
    setPricingFetchTrigger: React.Dispatch<React.SetStateAction<number | null>>;
    ahjModalOpen: boolean;
    setAhjModalOpen: (next: boolean) => void;
    ahjTown: string;
    setbackNotes: string[];
    addTitleContactsModalOpen: boolean;
    setAddTitleContactsModalOpen: (next: boolean) => void;
    titleSearchData: any;
    handleTitleSyncSuccess: (...args: any[]) => void;
    blackKnightModalOpen: boolean;
    setBlackKnightModalOpen: (next: boolean) => void;
    blackKnightModalData: any;
    opportunityID: any;
    oppValidationModalOpen: boolean;
    setOppValidationModalOpen: (next: boolean) => void;
    /** When true, stack financing grid above editor modals (e.g. tree cutting scenarios). */
    pricingGridElevatedStack?: boolean;
}

export const DashboardModalsSection: React.FC<DashboardModalsSectionProps> = (props) => {
    const {
        initialDashboardReady,
        isDashboardOpen,
        opportunityAssistantOpen,
        setOpportunityAssistantOpen,
        pricingGridModalOpen,
        setPricingGridModalOpen,
        theme,
        pricingFetchTrigger,
        pricingDesignForTrigger,
        priceQuote,
        setPricingFetchTrigger,
        ahjModalOpen,
        setAhjModalOpen,
        ahjTown,
        setbackNotes,
        addTitleContactsModalOpen,
        setAddTitleContactsModalOpen,
        titleSearchData,
        handleTitleSyncSuccess,
        blackKnightModalOpen,
        setBlackKnightModalOpen,
        blackKnightModalData,
        opportunityID,
        oppValidationModalOpen,
        setOppValidationModalOpen,
        pricingGridElevatedStack = false,
    } = props;

    const pricingGridZIndex = pricingGridElevatedStack
        ? OBD_PRICING_GRID_NESTED_MODAL_Z
        : OBD_PRICING_GRID_MODAL_Z;

    const pricingModalFetchTrigger = useOneButtonStore((s) => s.pricingModalFetchTrigger);

    useEffect(() => {
        const body = document.body;
        const html = document.documentElement;
        const openClass = 'obd-pricing-grid-modal-open';
        if (!body || !html) return;

        if (pricingGridModalOpen) {
            body.classList.add(openClass);
            html.classList.add(openClass);
        } else {
            body.classList.remove(openClass);
            html.classList.remove(openClass);
        }

        return () => {
            body.classList.remove(openClass);
            html.classList.remove(openClass);
        };
    }, [pricingGridModalOpen]);

    const hardCleanupPricingModalWrap = () => {
        const openClass = 'obd-pricing-grid-modal-open';
        document.body?.classList.remove(openClass);
        document.documentElement?.classList.remove(openClass);

        // Defensive cleanup: hidden AntD wrappers can remain in DOM and still capture clicks
        // when custom CSS forces modal-wrap layout.
        document
            .querySelectorAll('.obd-pricing-grid-modal-root--bento .ant-modal-wrap.ant-modal-wrap-hidden')
            .forEach((el) => {
                const node = el as HTMLElement;
                node.style.display = 'none';
                node.style.pointerEvents = 'none';
            });

        document
            .querySelectorAll('.obd-pricing-grid-modal-root--bento .ant-modal-mask.ant-modal-mask-hidden')
            .forEach((el) => {
                const node = el as HTMLElement;
                node.style.pointerEvents = 'none';
            });
    };

    useEffect(() => {
        if (!pricingGridModalOpen) return;

        const resetScroll = () => {
            const modalBody = document.querySelector(
                '.obd-pricing-grid-modal .obd-dashboard-modal__body',
            ) as HTMLElement | null;
            const financingWrap = document.querySelector(
                '.obd-pricing-grid-modal .obd-financing__wrap',
            ) as HTMLElement | null;

            if (modalBody) modalBody.scrollTop = 0;
            if (financingWrap) financingWrap.scrollTop = 0;
        };

        requestAnimationFrame(() => {
            resetScroll();
            setTimeout(resetScroll, 0);
        });
    }, [pricingGridModalOpen]);

    useEffect(() => {
        if (!pricingGridModalOpen) {
            hardCleanupPricingModalWrap();
        }
    }, [pricingGridModalOpen]);

    return (
        <>
            <OpportunityAssistantDrawer
                open={Boolean(initialDashboardReady && isDashboardOpen && opportunityAssistantOpen)}
                onClose={() => setOpportunityAssistantOpen(false)}
            />
            <DashboardModal
                open={pricingGridModalOpen}
                onClose={() => setPricingGridModalOpen(false)}
                footer={null}
                width={1200}
                title="Financing options"
                zIndex={pricingGridZIndex}
                className="obd-pricing-grid-modal"
                rootClassName={`obd-theme-${theme} obd-pricing-grid-modal-root--bento`}
                theme={theme === 'light' ? 'light' : 'dark'}
                destroyOnClose
                maskClosable
            >
                <FinancingGridCard
                    dashboardOpen={false}
                    fetchTrigger={pricingModalFetchTrigger ?? pricingFetchTrigger}
                    designForTrigger={pricingDesignForTrigger}
                    priceQuote={priceQuote}
                    onRefreshRequested={() => useOneButtonStore.setState({ pricingNetworkRefreshTrigger: Date.now() })}
                    embeddedInPricingModal
                />
            </DashboardModal>
            <DashboardModal
                title="AHJ Rules & Setback Notes"
                open={ahjModalOpen}
                onClose={() => setAhjModalOpen(false)}
                footer={null}
                width={560}
                zIndex={9610}
                theme={theme === 'light' ? 'light' : 'dark'}
                maskClosable
                destroyOnClose
                rootClassName={`obd-dashboard-ahj-modal-root obd-theme-${theme}`}
                className="obd-dashboard-ahj-modal"
            >
                <div className="obd-dashboard-ahj-modal__body" style={{ maxHeight: 'min(60vh, 520px)', overflow: 'auto' }}>
                    {ahjTown && (
                        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--ui-text, inherit)' }}>
                            <strong>AHJ Township:</strong> {ahjTown}
                        </div>
                    )}
                    {setbackNotes && setbackNotes.length > 0 ? (
                        <ul
                            style={{
                                paddingLeft: 18,
                                margin: 0,
                                fontSize: 13,
                                lineHeight: 1.5,
                                color: 'var(--ui-text, inherit)',
                            }}
                        >
                            {setbackNotes.map((note, idx) => (
                                <li key={idx} style={{ marginBottom: 6 }}>
                                    {note}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div style={{ fontSize: 13, color: 'var(--ui-muted, rgba(100, 116, 139, 0.9))' }}>
                            No AHJ setback notes available.
                        </div>
                    )}
                </div>
            </DashboardModal>

            <AddTitleContactsModal
                {...({
                    open: addTitleContactsModalOpen,
                    setOpen: setAddTitleContactsModalOpen,
                    initialData: titleSearchData,
                    onSuccess: handleTitleSyncSuccess,
                } as any)}
            />
            <BlackKnightLeadModal
                open={blackKnightModalOpen}
                setOpen={setBlackKnightModalOpen}
                data={blackKnightModalData}
                onSuccess={handleTitleSyncSuccess}
            />

            {opportunityID && (
                <OpportunityValidationModal
                    open={oppValidationModalOpen}
                    onCancel={() => setOppValidationModalOpen(false)}
                    opportunityId={opportunityID}
                    theme={theme === 'light' ? 'light' : 'dark'}
                />
            )}
        </>
    );
};
